/** @format */

import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';

import { BreakPointNew } from '@shared/data-access/enums';
import { DEVICE_BREAKPOINT_MEDIA_QUERIES } from '@plp/data-access/constants';
import { DeviceService, UtilityService } from '@shared/data-access/services';
import { isPlatformBrowser } from '@angular/common';
import { ProductListScrollToTopOptions } from '@plp/data-access/interfaces';

@Injectable({
  providedIn: 'root',
})
export class ProductListLayoutService {
  private readonly deviceService = inject(DeviceService);
  private readonly utilityService = inject(UtilityService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  readonly itemsPerRow = signal<number>(4);
  private readonly _scrollDirection = signal<'up' | 'down' | null>(null);
  readonly scrollDirection = this._scrollDirection.asReadonly();
  private matchMediaListeners: MediaQueryList[] = [];
  private plpDesktopTopContainer: HTMLElement | null = null;
  private plpMobileTopContainer: HTMLElement | null = null;
  private scrollTrackingFrame: number | null = null;
  private scrollEndListenerAttached = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopScrollTracking());
  }

  windowResizeHandler(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    Object.keys(DEVICE_BREAKPOINT_MEDIA_QUERIES).forEach((key) => {
      const deviceBreakPoint =
        DEVICE_BREAKPOINT_MEDIA_QUERIES[key as keyof typeof DEVICE_BREAKPOINT_MEDIA_QUERIES];
      const mediaQuery = globalThis.matchMedia(deviceBreakPoint);
      mediaQuery.onchange = () => {
        this.filteredMatchMediaCalls(mediaQuery);
      };
      this.matchMediaListeners.push(mediaQuery);
    });
  }

  private filteredMatchMediaCalls(mediaQuery: MediaQueryList): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (mediaQuery.matches) {
      this.setItemsPerRow(true);
    }
  }

  setItemsPerRow(forceCalculate = false): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const deviceBreakPoint = this.deviceService.getDeviceBreakPointNew(forceCalculate);

    switch (deviceBreakPoint) {
      case BreakPointNew.EXTRAEXTRASMALL:
        this.itemsPerRow.set(1);
        break;
      case BreakPointNew.EXTRASMALL:
      case BreakPointNew.SMALL:
        this.itemsPerRow.set(2);
        break;
      case BreakPointNew.MEDIUM:
      case BreakPointNew.LARGE:
      case BreakPointNew.EXTRALARGE:
        this.itemsPerRow.set(3);
        break;
      case BreakPointNew.EXTRAEXTRALARGE:
        this.itemsPerRow.set(4);
        break;
    }
  }

  private needsStickyHeaderOffset(topContainer: HTMLElement): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const topContainerPosition = topContainer.getBoundingClientRect().top;

    return topContainerPosition < 0;
  }

  scrollToTop(options: ProductListScrollToTopOptions = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.plpDesktopTopContainer ??= document.getElementById('plp-list-container');
    this.plpMobileTopContainer ??= document.getElementById('plp-srlp-content-wrapper');

    const isMobile = this.deviceService.getDeviceType() === 'mobile';
    const defaultTopContainer = isMobile ? this.plpMobileTopContainer : this.plpDesktopTopContainer;
    const elementToScroll = options.element || defaultTopContainer;

    if (!elementToScroll) {
      return;
    }

    // this offset is the sticky search bar height (72) to ensure the filter bar doesn't have a shadow
    // if the search bar changes in the future, this offset may need to be updated
    const offset = this.needsStickyHeaderOffset(elementToScroll) ? 72 : 0;

    const scrollBehavior = options.scrollBehavior || 'smooth';

    if (scrollBehavior === 'smooth') {
      this.startScrollTracking();
    }

    this.utilityService.scrollToElement(
      elementToScroll,
      {
        behavior: scrollBehavior,
        block: options.scrollBlock || 'start',
        inline: options.scrollInline || 'nearest',
      },
      isMobile ? offset : 0,
    );
  }

  private startScrollTracking(): void {
    this.stopScrollTracking();
    this._scrollDirection.set(null);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const supportsScrollEnd = 'onscrollend' in globalThis;
    let previousScrollY = globalThis.scrollY;
    let stableFrames = 0;
    const finish = (): void => this.stopScrollTracking();
    const checkScrollPosition = (): void => {
      this.scrollTrackingFrame = null;
      const currentScrollY = globalThis.scrollY;

      if (currentScrollY === previousScrollY) {
        stableFrames += 1;
      } else {
        this._scrollDirection.set(currentScrollY < previousScrollY ? 'up' : 'down');
        previousScrollY = currentScrollY;
        stableFrames = 0;
      }

      if (stableFrames >= 2) {
        finish();
        return;
      }

      this.scrollTrackingFrame = globalThis.requestAnimationFrame(checkScrollPosition);
    };

    if (supportsScrollEnd) {
      globalThis.addEventListener('scrollend', this.handleScrollEnd, { passive: true });
      this.scrollEndListenerAttached = true;
    }

    this.scrollTrackingFrame = globalThis.requestAnimationFrame(checkScrollPosition);
  }

  private stopScrollTracking(): void {
    if (this.scrollEndListenerAttached) {
      globalThis.removeEventListener('scrollend', this.handleScrollEnd);
      this.scrollEndListenerAttached = false;
    }

    if (this.scrollTrackingFrame !== null) {
      globalThis.cancelAnimationFrame(this.scrollTrackingFrame);
      this.scrollTrackingFrame = null;
    }

    this._scrollDirection.set(null);
  }

  private readonly handleScrollEnd = (): void => {
    this.stopScrollTracking();
  };

  cleanupListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.matchMediaListeners.forEach((mediaQuery) => {
      mediaQuery.onchange = null;
    });
    this.matchMediaListeners = [];
  }
}
